import { t } from '@lingui/core/macro'
import { ChartDataPoint, EventData } from 'features/systemLens'
import { Annotations, Shape } from 'plotly.js'

export const getThresholdColor = (type: string) => {
  switch (type) {
    case 'upperUpper':
      return '#dc2626'
    case 'upper':
      return '#f87171'
    case 'lower':
      return '#60a5fa'
    case 'lowerLower':
      return '#1d4ed8'
    default:
      return '#888'
  }
}

export const getThresholdLabel = (type: string) => {
  switch (type) {
    case 'upperUpper':
      return t`Upper High`
    case 'upper':
      return t`Upper`
    case 'lower':
      return t`Lower`
    case 'lowerLower':
      return t`Lower Low`
    default:
      return t`Threshold`
  }
}

export function getEventLineColor(priority?: string): string {
  switch (priority) {
    case 'Critical':
      return '#ef4444'
    case 'High':
      return '#f97316'
    case 'Medium':
      return '#fbbf24'
    case 'Low':
      return '#84cc16'
    default:
      return '#60a5fa'
  }
}

export function getEventLineWidth(priority?: string): number {
  switch (priority) {
    case 'Critical':
      return 3
    case 'High':
      return 2.5
    default:
      return 2
  }
}

export function getEventLineDash(status?: string): number[] {
  switch (status) {
    case 'Scheduled':
      return [8, 4]
    case 'In Progress':
      return [2, 2]
    default:
      return []
  }
}

function getEventLineDashStyle(status?: string): 'solid' | 'dot' | 'dash' {
  switch (status) {
    case 'Scheduled':
      return 'dash'
    case 'In Progress':
      return 'dot'
    default:
      return 'solid'
  }
}

/**
 * Generate shapes and annotations for operational events
 */
export function generateEventMarkers(
  events: EventData[],
  enabled: boolean
): {
  shapes: Partial<Shape>[]
  annotations: Partial<Annotations>[]
} {
  const shapes: Partial<Shape>[] = []
  const annotations: Partial<Annotations>[] = []

  if (!enabled || events.length === 0) {
    return { shapes, annotations }
  }

  events.forEach((event) => {
    const lineColor = getEventLineColor(event.metadata?.priority)
    const lineWidth = getEventLineWidth(event.metadata?.priority)
    const lineStyle = getEventLineDashStyle(event.metadata?.status)

    // Add vertical line
    shapes.push({
      type: 'line',
      x0: new Date(event.timestamp).toISOString(),
      x1: new Date(event.timestamp).toISOString(),
      xref: 'x',
      y0: 0,
      y1: 1,
      yref: 'paper',
      line: {
        color: lineColor,
        width: lineWidth,
        dash: lineStyle,
      },
    })

    // Add annotation with event details
    const annotationText = event.type

    annotations.push({
      x: new Date(event.timestamp).toISOString(),
      xref: 'x',
      y: 1,
      yref: 'paper',
      text: annotationText,
      showarrow: false,
      font: {
        size: 10,
        color: lineColor,
        // eslint-disable-next-line lingui/no-unlocalized-strings -- Font-family CSS, not user-facing copy.
        family: 'system-ui, -apple-system, sans-serif',
      },
      xanchor: 'center',
      yanchor: 'bottom',
      bgcolor: 'rgba(255, 255, 255, 0.8)',
      bordercolor: lineColor,
      borderwidth: 1,
      borderpad: 2,
    })
  })

  return { shapes, annotations }
}

export function getHeldValue(
  chartData: ChartDataPoint[],
  sensorId: string,
  xTimestamp: number
): number | null {
  let held: number | null = null
  for (const point of chartData) {
    const value = point[sensorId]
    if (typeof value !== 'number') continue
    if (point.timestamp > xTimestamp) return held
    held = value
    if (point.timestamp === xTimestamp) return held
  }
  return held
}

export function getInterpolatedValue(
  chartData: ChartDataPoint[],
  sensorId: string,
  xTimestamp: number
): number | null {
  let idx = -1
  for (let i = 0; i < chartData.length - 1; i++) {
    if (
      chartData[i].timestamp <= xTimestamp &&
      chartData[i + 1].timestamp >= xTimestamp
    ) {
      idx = i
      break
    }
  }
  // We need at least one point before and one after
  if (idx === -1 || idx >= chartData.length - 1) return null

  // Find nearest non-null value to the left and right
  let leftIdx = idx
  while (
    leftIdx >= 0 &&
    (chartData[leftIdx][sensorId] === null ||
      chartData[leftIdx][sensorId] === undefined)
  ) {
    leftIdx--
  }
  let rightIdx = idx + 1
  while (
    rightIdx < chartData.length &&
    (chartData[rightIdx][sensorId] === null ||
      chartData[rightIdx][sensorId] === undefined)
  ) {
    rightIdx++
  }

  // If we found both left and right indices, we can interpolate using y = mx + b
  if (leftIdx >= 0 && rightIdx < chartData.length) {
    const p1 = chartData[leftIdx]
    const p2 = chartData[rightIdx]
    const y1 = Number(p1[sensorId])
    const y2 = Number(p2[sensorId])
    const t1 = p1.timestamp
    const t2 = p2.timestamp

    if (t2 !== t1) {
      const slope = (y2 - y1) / (t2 - t1)
      return y1 + slope * (xTimestamp - t1)
    }
  }

  return null
}
